import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { CheckCircle, ArrowDown, Plus, MessageCircle, Settings } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { isMmMoneyCurrency } from '@vaiu/utils/mmMoney';
import { useRedeemUx } from './RedeemUxContext';
import IDTokenRedemptionResult from './IDTokenRedemptionResult';
import { FormattedCurrencyAmount, RedeemMmMoneyIssuerBanner } from './RedeemCurrencyDisplay';
import { leftoverAfterInvoicePay } from './redeemApiTypes';

export interface RedemptionResultProps {
  redemptionData: Record<string, unknown>;
  onClose: () => void;
}

const RedemptionResult = ({ redemptionData, onClose }: RedemptionResultProps) => {
  const { platformApi, Link, profileAutoDepositTo, InvoicePaymentReceived } = useRedeemUx();
  const [autoDepositEnabled, setAutoDepositEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAutoDeposit = async () => {
      try {
        const preferences = await platformApi.getPreferences<{
          autoDepositEnabled: boolean;
          autoDepositThreshold?: number;
        }>('auto-deposit');
        setAutoDepositEnabled(preferences.autoDepositEnabled || false);
      } catch (error) {
        console.error('Failed to load auto-deposit preferences:', error);
        setAutoDepositEnabled(false);
      }
    };
    checkAutoDeposit();
  }, [platformApi]);

  if (redemptionData?.isIDToken) {
    return (
      <IDTokenRedemptionResult
        result={redemptionData as Record<string, unknown>}
        tokenKey={String(redemptionData.tokenKey ?? '')}
        onClose={onClose}
      />
    );
  }

  const amount =
    (redemptionData.amount as number) ||
    (redemptionData.paymentAmount as number) ||
    (redemptionData.redeemAmount as number) ||
    (redemptionData.netAmount as number) ||
    0;
  const balance =
    (redemptionData.balance as number) || (redemptionData.newBalance as number) || 0;
  const details = (redemptionData.details as Record<string, unknown>) || {};

  const paidInvoice = (details.paidInvoice as string) || (redemptionData.paidInvoice as string);
  const isInvoicePayment =
    !!paidInvoice ||
    details.transactionType === 'invoice' ||
    redemptionData.transactionType === 'invoice';

  const currency =
    (redemptionData.currency as string) ||
    (details.currency as string) ||
    ((details.data as Record<string, unknown>)?.currency as string) ||
    'USD';

  const transactionFee =
    (redemptionData.transactionFee as number) ||
    (details.transactionFee as number) ||
    (details.fee as number) ||
    0;

  const netAmount =
    (details.netAmount as number) ||
    (redemptionData.netAmount as number) ||
    amount - transactionFee;

  const displayMessage =
    (details.message as string) ||
    ((details.data as Record<string, unknown>)?.message as string) ||
    (redemptionData.message as string);

  if (isInvoicePayment) {
    const remainingAmount = leftoverAfterInvoicePay(redemptionData);
    const isPartialPayment =
      redemptionData.status === 'partial' ||
      (typeof remainingAmount === 'number' && remainingAmount > 0);
    const conditions = redemptionData.conditions as Record<string, unknown> | undefined;

    return (
      <InvoicePaymentReceived
        invoiceCode={paidInvoice}
        paymentAmount={amount}
        currency={currency}
        transactionFee={transactionFee}
        newBalance={balance}
        onContinue={onClose}
        message={displayMessage}
        redeemer={
          (redemptionData.redeemer as string) ||
          (conditions?.redeemer as { name?: string } | undefined)?.name
        }
        remainder={remainingAmount}
        status={isPartialPayment ? 'partial' : 'completed'}
        displayMode="inline"
      />
    );
  }

  return (
    <Card className="border-green-200 bg-green-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-green-800">
          <CheckCircle className="w-5 h-5" />
          <span>Cash Token Redeemed Successfully</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isMmMoneyCurrency(currency) && <RedeemMmMoneyIssuerBanner currencyKey={currency} />}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <span className="text-sm text-muted-foreground">Token Value</span>
            <span className="font-semibold text-lg">
              <FormattedCurrencyAmount amount={amount} currency={currency} />
            </span>
          </div>

          <div className="flex justify-center">
            <ArrowDown className="w-4 h-4 text-muted-foreground" />
          </div>

          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
            <span className="text-sm text-red-600">Processing Fee</span>
            <span className="font-medium text-red-600">
              -<FormattedCurrencyAmount amount={transactionFee} currency={currency} />
            </span>
          </div>

          <div className="flex justify-center">
            <Plus className="w-4 h-4 text-green-600" />
          </div>

          <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-300">
            <span className="text-sm text-green-700">Amount Received</span>
            <span className="font-semibold text-lg text-green-700">
              <FormattedCurrencyAmount amount={netAmount} currency={currency} />
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm text-blue-700">New Balance</span>
            <span className="font-semibold text-lg text-blue-700">
              <FormattedCurrencyAmount amount={balance} currency={currency} />
            </span>
          </div>

          {displayMessage && (
            <div className="mt-4 relative">
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 shadow-sm relative">
                <div className="flex items-start space-x-3">
                  <MessageCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-800 italic">&quot;{displayMessage}&quot;</p>
                </div>
                <div className="absolute bottom-0 left-6 transform translate-y-full">
                  <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-gray-200" />
                  <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white absolute top-0 left-[2px]" />
                </div>
              </div>
            </div>
          )}

          {!isInvoicePayment && autoDepositEnabled === false && (
            <div className="mt-6">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Settings className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 text-sm">Want to skip manual confirmation next time?</p>
                    <Link
                      to={profileAutoDepositTo}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center mt-1"
                    >
                      Enable auto-deposit in settings
                      <ArrowDown className="w-4 h-4 ml-1 rotate-[-90deg]" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Button onClick={onClose} className="w-full mt-4">
          Continue
        </Button>
      </CardContent>
    </Card>
  );
};

export default RedemptionResult;
